const HEIC_EXTENSIONS = /\.(heic|heif)$/i

const isHeicFile = (file: File): boolean => {
  return file.type === "image/heic" || file.type === "image/heif" || HEIC_EXTENSIONS.test(file.name)
}

const isAlreadyReadableError = (err: unknown): boolean => {
  if (!err || typeof err !== "object") return false
  const e = err as { code?: number; message?: string }
  if (e.code === 1) return true
  return typeof e.message === "string" && /already browser readable/i.test(e.message)
}

const decodeViaHeic2any = async (file: Blob): Promise<Blob> => {
  const heic2any = (await import("heic2any")).default
  const result = await heic2any({ blob: file, toType: "image/jpeg", quality: 0.9 })
  return Array.isArray(result) ? result[0] : result
}

const decode = async (file: File): Promise<ImageBitmap> => {
  try {
    return await createImageBitmap(file, { imageOrientation: "from-image" })
  } catch (nativeErr) {
    if (!isHeicFile(file)) throw nativeErr

    try {
      const jpegBlob = await decodeViaHeic2any(file)
      return await createImageBitmap(jpegBlob, { imageOrientation: "from-image" })
    } catch (heicErr) {
      if (!isAlreadyReadableError(heicErr)) throw heicErr
      const retyped = new Blob([file], { type: "image/jpeg" })
      return await createImageBitmap(retyped, { imageOrientation: "from-image" })
    }
  }
}

const bitmapToJpegFile = async (bitmap: ImageBitmap, source: File): Promise<File> => {
  const canvas = document.createElement("canvas")
  canvas.width = bitmap.width
  canvas.height = bitmap.height
  const ctx = canvas.getContext("2d")
  if (!ctx) throw new Error("Canvas 2d context unavailable")
  ctx.drawImage(bitmap, 0, 0)

  const blob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error("canvas.toBlob returned null"))),
      "image/jpeg",
      0.9
    )
  })

  const baseName = source.name.replace(/\.[^.]+$/, "") || "photo"
  return new File([blob], `${baseName}.jpg`, { type: "image/jpeg" })
}

export const normalizeImage = async (file: File): Promise<File> => {
  const bitmap = await decode(file)
  try {
    return await bitmapToJpegFile(bitmap, file)
  } finally {
    bitmap.close()
  }
}
